/**
 * Centralized external/app links (APP-001 §14, §12 action validation).
 *
 * Server-provided action URLs are validated against this allowlist before
 * navigation (see src/lib/redirect.ts). Only same-origin application routes,
 * approved first-party Webflow/docs origins, and Stripe-hosted checkout/portal
 * URLs returned by billing endpoints are ever navigated to.
 *
 * Legal + support destinations are pinned to the canonical www marketing host
 * so local/dev marketing-base drift cannot point them at a dead domain.
 * Docs destinations follow Scribe canonical paths (2026-09-24): never /api.
 */
import { loadConfig } from './env';

/** Canonical marketing pages (Lumen / Webflow). */
export const CANONICAL_PRIVACY_URL = 'https://www.signuprisk.com/privacy';
export const CANONICAL_TERMS_URL = 'https://www.signuprisk.com/terms';
export const CANONICAL_SUPPORT_URL = 'https://www.signuprisk.com/support';

export interface AppLinks {
  marketingBase: string;
  docsBase: string;
  /** "API Documentation" label — Mintlify API reference entry. */
  apiDocs: string;
  /** Bulk scoring docs (API ref; no conceptual bulk guide yet). */
  bulkDocs: string;
  /** General Docs / View docs CTA. */
  quickstart: string;
  errorReference: string;
  support: string;
  privacy: string;
  terms: string;
}

function stripTrailingSlash(url: string): string {
  return url.replace(/\/$/, '');
}

export function getAppLinks(): AppLinks {
  const { marketingBaseUrl, docsBaseUrl } = loadConfig();
  const docsBase = stripTrailingSlash(docsBaseUrl);
  return {
    marketingBase: marketingBaseUrl,
    docsBase,
    // Scribe/Atlas: API Documentation → /api-reference (NOT /api, NOT bare root).
    apiDocs: `${docsBase}/api-reference`,
    bulkDocs: `${docsBase}/api-reference/bulk/create-a-bulk-job-from-a-csv-file`,
    quickstart: `${docsBase}/quickstart`,
    errorReference: `${docsBase}/errors`,
    // Founder decision B: www /support (brief 404 OK until Lumen publishes).
    support: CANONICAL_SUPPORT_URL,
    privacy: CANONICAL_PRIVACY_URL,
    terms: CANONICAL_TERMS_URL,
  };
}

/** Approved external origins for navigation (APP-001 §12). */
export const APPROVED_EXTERNAL_HOSTS = new Set<string>([
  'signuprisk.com',
  'www.signuprisk.com',
  'docs.signuprisk.com',
  'app.signuprisk.com',
  'api.signuprisk.com',
  // Stripe-hosted checkout/portal pages are returned by billing endpoints.
  'checkout.stripe.com',
  'billing.stripe.com',
]);
